import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  applyChatRename,
  chatNamesMatch,
  type ChatMessagePayload,
  type DirectMessagePayload,
  type Seat,
} from '@shared/index'
import { fetchDirect, markDirectRead } from '../api/friends'
import { useAuthStore } from './auth'
import { useChatMuteStore } from './chatMutes'
import { useFriendsStore } from './friends'
import { usePlayerStore } from './player'

export type ChatTab = 'global' | 'duel' | 'dm'

export type ChatNotice = {
  id: string
  tab: ChatTab
  text: string
  at: number
}

export type DmLine = {
  id: string
  username: string
  text: string
  at: number
  mine: boolean
}

const DM_TABS_KEY = 'dc.dmTabs'
const DM_TAB_CAP = 20

function dismissId(name: string): string {
  return name.trim().toLowerCase()
}

function readStoredTabs(): string[] {
  try {
    const raw = localStorage.getItem(DM_TABS_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((name): name is string => typeof name === 'string' && name.trim().length > 0)
      .slice(0, DM_TAB_CAP)
  } catch {
    return []
  }
}

/**
 * Coordinates the site chat panel: open state, Global vs Duel vs friend
 * tabs, and duel-channel messages owned by the live PvP client.
 */
export const useChatStore = defineStore('chat', () => {
  const open = ref(false)
  const tab = ref<ChatTab>('global')
  const globalUnread = ref(0)
  const duelUnread = ref(0)
  /**
   * The matching log is open, the browser tab is visible, and the
   * latest line is on screen. A message then is already in view.
   */
  const globalInView = ref(false)
  const duelInView = ref(false)
  const dmInView = ref(false)
  const duelAvailable = ref(false)
  const duelMessages = ref<ChatMessagePayload[]>([])
  const notices = ref<ChatNotice[]>([])
  const mySeat = ref<Seat>(0)
  /** The other human in the live PvP, when the Duel tab is attached. */
  const duelPeer = ref<{ username: string; connected: boolean } | null>(null)
  const dmTabs = ref<string[]>(readStoredTabs())
  const dmActive = ref<string | null>(null)
  const dmThreads = ref<Record<string, DmLine[]>>({})
  const dmUnread = ref<Record<string, number>>({})
  /** Closed this session. A newer line opens the tab again. */
  const dmDismissed = new Set<string>()
  const invite = ref<{ username: string; code: string } | null>(null)
  const loaded = new Set<string>()
  const marking = new Set<string>()
  let sendDuelFn: ((text: string) => void) | null = null
  let sendDmFn: ((username: string, text: string) => void) | null = null
  let declineInviteFn: ((username: string) => void) | null = null

  const closedBadge = computed(() => {
    const global = globalInView.value ? 0 : globalUnread.value
    const duel = duelAvailable.value && !duelInView.value ? duelUnread.value : 0
    let direct = 0
    for (const [name, count] of Object.entries(dmUnread.value)) {
      if (!dmTabs.value.some((row) => chatNamesMatch(row, name))) continue
      if (dmInView.value && dmActive.value && chatNamesMatch(dmActive.value, name)) continue
      direct += count
    }
    return global + duel + direct
  })

  function persistTabs(): void {
    try {
      localStorage.setItem(DM_TABS_KEY, JSON.stringify(dmTabs.value))
    } catch {
      /* private mode */
    }
  }

  function keyFor(name: string): string {
    const tabName = dmTabs.value.find((row) => chatNamesMatch(row, name))
    if (tabName) return tabName
    const unreadName = Object.keys(dmUnread.value).find((row) => chatNamesMatch(row, name))
    if (unreadName) return unreadName
    const threadName = Object.keys(dmThreads.value).find((row) => chatNamesMatch(row, name))
    return threadName ?? name
  }

  function close(): void {
    open.value = false
    globalInView.value = false
    duelInView.value = false
    dmInView.value = false
  }

  /**
   * Opens or closes the panel. Opening clears unread on the active tab.
   */
  function toggle(): void {
    open.value = !open.value
    if (open.value) {
      clearUnread(tab.value)
      if (tab.value === 'dm' && dmActive.value) void markDmRead(dmActive.value)
    } else {
      globalInView.value = false
      duelInView.value = false
      dmInView.value = false
    }
  }

  /**
   * Switches channel. Clears unread on the tab that becomes visible.
   */
  function setTab(next: ChatTab): void {
    if (next === 'duel' && !duelAvailable.value) return
    if (next === 'dm' && !dmActive.value) return
    tab.value = next
    if (next !== 'global') globalInView.value = false
    if (next !== 'duel') duelInView.value = false
    if (next !== 'dm') dmInView.value = false
    if (open.value) clearUnread(next)
  }

  /**
   * The panel reports whether that log is actually showing the latest line.
   * Seeing it clears the count. Looking away starts a new count.
   */
  function setChannelInView(which: ChatTab, inView: boolean): void {
    if (which === 'dm') {
      const had =
        dmActive.value !== null && (dmUnread.value[keyFor(dmActive.value)] ?? 0) > 0
      dmInView.value = inView
      if (inView) clearUnread(which)
      if (inView && had && dmActive.value) void markDmRead(dmActive.value)
      return
    }
    if (which === 'global') globalInView.value = inView
    else duelInView.value = inView
    if (inView) clearUnread(which)
  }

  function clearUnread(which: ChatTab): void {
    if (which === 'global') globalUnread.value = 0
    else if (which === 'duel') duelUnread.value = 0
    else if (dmActive.value) {
      const key = keyFor(dmActive.value)
      if ((dmUnread.value[key] ?? 0) !== 0) {
        dmUnread.value = { ...dmUnread.value, [key]: 0 }
      }
    }
  }

  /**
   * Increments the global badge unless that log is showing the latest line.
   */
  function bumpGlobalUnread(isMine: boolean, author?: string): void {
    if (isMine) return
    const muted = useChatMuteStore()
    if (!muted.ready) return
    if (author && muted.isMuted(author)) return
    if (globalInView.value) return
    globalUnread.value += 1
  }

  /**
   * Hooks the PvP duel client so the Duel tab can send.
   */
  function attachDuel(opts: {
    send: (text: string) => void
    mySeat: Seat
    peer?: { username: string; connected: boolean } | null
  }): void {
    sendDuelFn = opts.send
    mySeat.value = opts.mySeat
    duelPeer.value = opts.peer ?? null
    duelAvailable.value = true
    if (!open.value) tab.value = 'duel'
  }

  /**
   * Drops the Duel tab when the PvP match client is gone.
   */
  function detachDuel(): void {
    sendDuelFn = null
    duelPeer.value = null
    duelAvailable.value = false
    duelMessages.value = []
    duelUnread.value = 0
    duelInView.value = false
    if (tab.value === 'duel') tab.value = 'global'
  }

  function setMySeat(seat: Seat): void {
    mySeat.value = seat
  }

  function setDuelHistory(messages: ChatMessagePayload[]): void {
    duelMessages.value = messages
    duelUnread.value = 0
  }

  function renameMapKey<T>(map: Record<string, T>, previous: string, username: string): Record<string, T> {
    const next: Record<string, T> = {}
    for (const [name, value] of Object.entries(map)) {
      next[chatNamesMatch(name, previous) ? username : name] = value
    }
    return next
  }

  /**
   * Author labels and @mentions already on screen.
   */
  function applyRename(previous: string, username: string): void {
    duelMessages.value = duelMessages.value.map((message) =>
      applyChatRename(message, previous, username),
    )
    const peer = duelPeer.value
    if (peer && chatNamesMatch(peer.username, previous)) {
      duelPeer.value = { ...peer, username }
    }
    dmTabs.value = dmTabs.value.map((name) => (chatNamesMatch(name, previous) ? username : name))
    if (dmActive.value && chatNamesMatch(dmActive.value, previous)) dmActive.value = username
    const previousId = dismissId(previous)
    if (dmDismissed.has(previousId)) {
      dmDismissed.delete(previousId)
      dmDismissed.add(dismissId(username))
    }
    dmThreads.value = renameMapKey(dmThreads.value, previous, username)
    dmUnread.value = renameMapKey(dmUnread.value, previous, username)
    for (const name of [...loaded]) {
      if (!chatNamesMatch(name, previous)) continue
      loaded.delete(name)
      loaded.add(username)
    }
    if (invite.value && chatNamesMatch(invite.value.username, previous)) {
      invite.value = { ...invite.value, username }
    }
    persistTabs()
  }

  function pushDuelMessage(message: ChatMessagePayload): void {
    duelMessages.value = [...duelMessages.value, message]
    const mine = message.seat === mySeat.value
    if (mine) return
    const muted = useChatMuteStore()
    if (!muted.ready || muted.isMuted(message.username)) return
    if (duelInView.value) return
    duelUnread.value += 1
  }

  /**
   * A line only this client sees, such as the result of /mute.
   */
  function pushNotice(which: ChatTab, text: string): void {
    const id = `n${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`
    const next = [...notices.value, { id, tab: which, text, at: Date.now() }]
    notices.value = next.length > 20 ? next.slice(next.length - 20) : next
  }

  function clearNotices(): void {
    notices.value = []
  }

  /**
   * Sends on the Duel tab. No-op if no PvP client is attached.
   */
  function sendDuel(text: string): void {
    sendDuelFn?.(text)
  }

  function bindSite(fns: {
    sendDm: (username: string, text: string) => void
    declineInvite: (username: string) => void
  }): void {
    sendDmFn = fns.sendDm
    declineInviteFn = fns.declineInvite
  }

  function dmLines(username: string): DmLine[] {
    return dmThreads.value[keyFor(username)] ?? []
  }

  function dmUnreadOf(username: string): number {
    return dmUnread.value[keyFor(username)] ?? 0
  }

  function bumpDm(name: string): void {
    const key = keyFor(name)
    dmUnread.value = { ...dmUnread.value, [key]: (dmUnread.value[key] ?? 0) + 1 }
  }

  /** A private chat with unread lines stays as a tab, so the badge has a place. */
  function rememberTab(name: string): string {
    const key = keyFor(name)
    if (!dmTabs.value.some((row) => chatNamesMatch(row, key))) {
      dmTabs.value = [...dmTabs.value, key].slice(-DM_TAB_CAP)
      persistTabs()
    }
    return dmTabs.value.find((row) => chatNamesMatch(row, key)) ?? key
  }

  function appendLine(name: string, line: DmLine): void {
    const key = keyFor(name)
    const prev = dmThreads.value[key] ?? []
    if (prev.some((row) => row.id === line.id)) return
    dmThreads.value = { ...dmThreads.value, [key]: [...prev, line] }
  }

  async function loadThread(username: string): Promise<void> {
    const auth = useAuthStore().token
    if (!auth) return
    const key = keyFor(username)
    try {
      const { messages } = await fetchDirect(auth, key)
      const me = usePlayerStore().username
      const lines: DmLine[] = (messages ?? []).map((message) => ({
        id: message.id,
        username: message.from,
        text: message.text,
        at: message.at,
        mine: chatNamesMatch(message.from, me),
      }))
      const prev = dmThreads.value[key] ?? []
      const ids = new Set(lines.map((line) => line.id))
      const extra = prev.filter((line) => !ids.has(line.id))
      dmThreads.value = {
        ...dmThreads.value,
        [key]: [...lines, ...extra].sort((a, b) => a.at - b.at),
      }
      loaded.add(key)
    } catch {
      /* live lines stay */
    }
  }

  async function markDmRead(username: string): Promise<void> {
    const key = keyFor(username)
    const auth = useAuthStore().token
    if (!auth || marking.has(key)) return
    marking.add(key)
    if ((dmUnread.value[key] ?? 0) !== 0) {
      dmUnread.value = { ...dmUnread.value, [key]: 0 }
    }
    useFriendsStore().noteRead(key)
    try {
      await markDirectRead(auth, key)
    } catch {
      /* the next view retries */
    } finally {
      marking.delete(key)
    }
  }

  /** Opens the panel on this friend. Does not create a tab for every friend. */
  async function openDm(username: string): Promise<void> {
    const name = username.trim()
    if (!name) return
    dmDismissed.delete(dismissId(name))
    const key = keyFor(name)
    if (!dmTabs.value.some((row) => chatNamesMatch(row, key))) {
      dmTabs.value = [...dmTabs.value, key].slice(-DM_TAB_CAP)
    }
    dmActive.value = dmTabs.value.find((row) => chatNamesMatch(row, key)) ?? key
    tab.value = 'dm'
    open.value = true
    persistTabs()
    await loadThread(dmActive.value)
    void markDmRead(dmActive.value)
  }

  function focusDm(username: string): void {
    const stored = dmTabs.value.find((row) => chatNamesMatch(row, username))
    if (!stored) {
      void openDm(username)
      return
    }
    dmActive.value = stored
    tab.value = 'dm'
    open.value = true
    if (!loaded.has(stored)) void loadThread(stored)
    void markDmRead(stored)
  }

  function closeDm(username: string, opts?: { dropUnread?: boolean }): void {
    const stored = dmTabs.value.find((row) => chatNamesMatch(row, username))
    const key = stored ?? keyFor(username)
    if (stored) dmTabs.value = dmTabs.value.filter((row) => row !== stored)
    dmDismissed.add(dismissId(key))
    if (opts?.dropUnread) {
      const next = { ...dmUnread.value }
      delete next[key]
      dmUnread.value = next
    }
    const threads = { ...dmThreads.value }
    delete threads[key]
    dmThreads.value = threads
    loaded.delete(key)
    if (dmActive.value && chatNamesMatch(dmActive.value, key)) {
      dmActive.value = null
      dmInView.value = false
      if (tab.value === 'dm') tab.value = 'global'
    }
    persistTabs()
  }

  /** Drops tabs for accounts that are no longer friends. */
  function pruneDm(friendNames: readonly string[]): void {
    for (const name of [...dmTabs.value]) {
      if (friendNames.some((friend) => chatNamesMatch(friend, name))) continue
      closeDm(name, { dropUnread: true })
    }
    const next = { ...dmUnread.value }
    let changed = false
    for (const name of Object.keys(next)) {
      if (friendNames.some((friend) => chatNamesMatch(friend, name))) continue
      delete next[name]
      changed = true
    }
    if (changed) dmUnread.value = next
  }

  function seedDmUnread(rows: readonly { username: string; unread: number }[]): void {
    const next = { ...dmUnread.value }
    for (const row of rows) {
      const key = keyFor(row.username)
      const prev = next[key] ?? 0
      const unread = Math.max(prev, row.unread)
      next[key] = unread
      const id = dismissId(key)
      if (unread > prev) dmDismissed.delete(id)
      if (unread > 0 && !dmDismissed.has(id)) rememberTab(row.username)
    }
    dmUnread.value = next
  }

  function ingestDm(message: DirectMessagePayload): void {
    if (!message || typeof message.id !== 'string' || typeof message.from !== 'string') return
    if (typeof message.text !== 'string' || typeof message.to !== 'string') return
    const mine = chatNamesMatch(message.from, usePlayerStore().username)
    const peer = mine ? message.to : message.from
    if (!peer.trim()) return
    dmDismissed.delete(dismissId(peer))
    const key = rememberTab(peer)
    appendLine(key, {
      id: message.id,
      username: message.from,
      text: message.text,
      at: typeof message.at === 'number' ? message.at : Date.now(),
      mine,
    })
    const viewing =
      dmInView.value && dmActive.value !== null && chatNamesMatch(dmActive.value, key)
    if (mine || viewing) {
      if (viewing) void markDmRead(key)
      return
    }
    bumpDm(key)
  }

  function sendDm(text: string): void {
    const name = dmActive.value
    if (!name) return
    sendDmFn?.(name, text)
  }

  function pushInvite(payload: { username?: string; code?: string }): void {
    const username = payload.username?.trim() ?? ''
    const code = payload.code?.trim().toUpperCase() ?? ''
    if (!username || code.length !== 4) return
    invite.value = { username, code }
  }

  function clearInvite(): void {
    invite.value = null
  }

  function declineInvite(): void {
    const current = invite.value
    if (!current) return
    declineInviteFn?.(current.username)
    invite.value = null
  }

  /** Drops open friend tabs. Used when the account signs out. */
  function clearDirect(): void {
    dmTabs.value = []
    dmActive.value = null
    dmThreads.value = {}
    dmUnread.value = {}
    dmDismissed.clear()
    dmInView.value = false
    invite.value = null
    loaded.clear()
    if (tab.value === 'dm') tab.value = 'global'
    persistTabs()
  }

  return {
    open,
    tab,
    globalUnread,
    duelUnread,
    duelAvailable,
    duelMessages,
    notices,
    mySeat,
    duelPeer,
    dmTabs,
    dmActive,
    invite,
    closedBadge,
    toggle,
    close,
    setTab,
    setChannelInView,
    bumpGlobalUnread,
    attachDuel,
    detachDuel,
    setMySeat,
    setDuelHistory,
    applyRename,
    pushDuelMessage,
    pushNotice,
    clearNotices,
    sendDuel,
    bindSite,
    dmLines,
    dmUnreadOf,
    openDm,
    focusDm,
    closeDm,
    pruneDm,
    seedDmUnread,
    ingestDm,
    sendDm,
    pushInvite,
    clearInvite,
    declineInvite,
    clearDirect,
  }
})
